/**
 * Task Pane UI
 */

/* global document, Office */

import "./taskpane.css";
import "../domain/functionCatalog";
import { getProxyBaseUrlInUse } from "../services/proxyClient";
import { authStore } from "../shared/auth";
import { refreshAccountUI, setEntitlementUI } from "./ui/account";
import { getEntitlements } from "../services/entitlements";
import { STORAGE_KEYS } from "../shared/constants";
import { logger } from "../shared/logger";
import { COMMAND_HANDLERS } from "../domain/commandRouter";
import type { StoredPingResult } from "../shared/types";
import { initializeDiagnosticsPanel } from "./diagnosticsPanel";
import { renderBuildBadge } from "./buildBadge";
import { initializeSupportSnapshot } from "./supportSnapshot";
import { OPTION_ENTITY_V1_FIELDS } from "../excel/entities/optionEntity.schema";
import { getOptionPropertyName } from "../excel/entities/optionEntity";
import { DEFAULT_CHAIN_FIELD_KEYS } from "../excel/chains/chainFields";
import { insertChain, updateChainPreview, upgradeToPro, refreshEntitlementsAndUpdateAccountUI } from "./taskpaneHandlers";
import { refreshAfterFocusIfNeeded } from "./stripeReturnCoordinator";

Office.onReady((info) => {
  if (info.host === Office.HostType.Excel) {
    document.getElementById("sideload-msg")!.style.display = "none";
    document.getElementById("app-body")!.style.display = "flex";
    initializeUI();
  }
});

async function initializeUI(): Promise<void> {
  // Load proxy base URL
  await updateProxyInfo();

  // Load account state
  const session = await authStore.load();
  refreshAccountUI(session);
  try {
    const ent = await getEntitlements();
    setEntitlementUI({ plan: ent.plan, status: ent.status, stripe_customer_id: ent.stripe_customer_id, current_period_end: ent.current_period_end });
  } catch {
    setEntitlementUI(null);
  }

  // Interval refresh: first tick 5-15s after load, then every 5 min
  const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
  const staggerMs = 5000 + Math.random() * 10000;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  const doRefresh = (): void => {
    void getEntitlements().then((ent) => {
      setEntitlementUI({ plan: ent.plan, status: ent.status, stripe_customer_id: ent.stripe_customer_id, current_period_end: ent.current_period_end });
    }).catch(() => {});
  };
  setTimeout(() => {
    doRefresh();
    intervalId = setInterval(doRefresh, REFRESH_INTERVAL_MS);
  }, staggerMs);

  window.addEventListener("beforeunload", () => {
    if (intervalId) clearInterval(intervalId);
  });

  // Focus/visibility fallback: when returning from Stripe in external tab, refresh entitlements once
  const onVisibilityOrFocus = (): void => {
    void refreshAfterFocusIfNeeded("visibility_focus", refreshEntitlementsAndUpdateAccountUI);
  };
  document.addEventListener("visibilitychange", onVisibilityOrFocus);
  window.addEventListener("focus", onVisibilityOrFocus);

  // Load last ping result
  await refreshPingResult();

  // Wire up button handlers via command router
  document.getElementById("sign-in-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_signIn"]();
  };
  document.getElementById("sign-out-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_signOut"]();
  };
  document.getElementById("upgrade-monthly-btn")!.onclick = () => {
    void upgradeToPro("monthly");
  };
  document.getElementById("upgrade-annual-btn")!.onclick = () => {
    void upgradeToPro("annual");
  };
  document.getElementById("manage-subscription-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_manageSubscription"]();
  };
  document.getElementById("refresh-license-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_refreshEntitlements"]();
  };
  document.getElementById("copy-token-btn")!.onclick = async () => {
    const statusEl = document.getElementById("status-message")!;
    try {
      const session = await authStore.load();
      if (!session?.access_token) {
        statusEl.textContent = "Not signed in.";
        statusEl.style.color = "#d13438";
        setTimeout(() => { statusEl.textContent = ""; }, 3000);
        return;
      }
      await navigator.clipboard.writeText(session.access_token);
      statusEl.textContent = "Token copied to clipboard.";
      statusEl.style.color = "#107c10";
      setTimeout(() => { statusEl.textContent = ""; }, 3000);
    } catch (err) {
      statusEl.textContent = "Could not copy token.";
      statusEl.style.color = "#d13438";
      setTimeout(() => { statusEl.textContent = ""; }, 3000);
    }
  };
  const otpLink = document.getElementById("otp-fallback-link");
  const statusEl = document.getElementById("status-message");
  if (otpLink && statusEl) {
    otpLink.onclick = (e) => {
      e.preventDefault();
      const handler = COMMAND_HANDLERS["tp_signInOtp"];
      if (handler) {
        void handler();
      } else {
        statusEl.textContent = "Email code sign-in coming soon.";
        statusEl.style.color = "#666";
        setTimeout(() => {
          statusEl.textContent = "";
        }, 3000);
      }
    };
  }
  const clearSessionLink = document.getElementById("clear-session-link");
  if (clearSessionLink) {
    clearSessionLink.onclick = (e) => {
      e.preventDefault();
      void COMMAND_HANDLERS["tp_signOut"]();
    };
  }
  document.getElementById("ping-proxy-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_pingProxy"]();
  };
  document.getElementById("refresh-ping-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_refreshPing"]();
  };
  document.getElementById("auth-test-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_authTest"]();
  };
  document.getElementById("insert-ping-result-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_insertPingResult"]();
  };
  document.getElementById("fetch-market-holidays-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_fetchMarketHolidays"]();
  };
  document.getElementById("insert-market-is-open-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_insertMarketIsOpen"]();
  };
  document.getElementById("insert-market-next-trading-day-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_insertMarketNextTradingDay"]();
  };
  document.getElementById("insert-market-next-session-open-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_insertMarketNextSessionOpen"]();
  };
  document.getElementById("insert-market-next-session-close-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_insertMarketNextSessionClose"]();
  };
  document.getElementById("insert-option-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_insertOptionFromTaskPane"]();
  };
  document.getElementById("add-columns-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_addColumns"]();
  };
  document.getElementById("wrap-formulas-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_wrapFormulas"]();
  };
  document.getElementById("refresh-selected-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_refreshSelectedOptions"]();
  };
  document.getElementById("refresh-sheet-options-btn")!.onclick = () => {
    void COMMAND_HANDLERS["tp_refreshSheetOptions"]();
  };

  // Initialize Options UI
  void initializeOptionsUI();

  // Initialize Chain Builder UI
  void initializeChainBuilderUI();

  // Initialize support snapshot (always visible, prod-safe)
  void initializeSupportSnapshot();

  // Initialize diagnostics panel (dev-only, feature-flagged)
  void initializeDiagnosticsPanel();

  // Render build badge (always visible)
  void renderBuildBadge();
}

async function updateProxyInfo(): Promise<void> {
  const proxyBaseUrl = await getProxyBaseUrlInUse();
  const proxyInfoEl = document.getElementById("proxy-base-url")!;
  proxyInfoEl.textContent = "Base URL: " + proxyBaseUrl;
  logger.log("Taskpane", "proxy base URL", proxyBaseUrl);
}


async function refreshPingResult(): Promise<void> {
  const pingResultEl = document.getElementById("ping-result")!;

  try {
    const storedJson = await OfficeRuntime.storage.getItem(STORAGE_KEYS.LAST_PING_RESULT);
    if (storedJson) {
      const stored = JSON.parse(storedJson as string) as StoredPingResult;
      pingResultEl.textContent = JSON.stringify(stored, null, 2);
      logger.log("Taskpane", "refresh ping result", "success");
    } else {
      pingResultEl.textContent = "No ping result stored yet";
    }
  } catch (error) {
    logger.error("Taskpane", "refresh ping result failed", error);
    pingResultEl.textContent = "Error loading ping result";
  }
}

function initializeOptionsUI(): void {
  // Mode toggle handlers
  const symbolModeRadio = document.getElementById("option-mode-symbol") as HTMLInputElement;
  const detailsModeRadio = document.getElementById("option-mode-details") as HTMLInputElement;
  const symbolModePanel = document.getElementById("option-symbol-mode")!;
  const detailsModePanel = document.getElementById("option-details-mode")!;

  function updateModeDisplay(): void {
    if (symbolModeRadio.checked) {
      symbolModePanel.classList.remove("option-mode-panel-hidden");
      detailsModePanel.classList.add("option-mode-panel-hidden");
    } else {
      symbolModePanel.classList.add("option-mode-panel-hidden");
      detailsModePanel.classList.remove("option-mode-panel-hidden");
    }
    // Update preview when mode changes
    void COMMAND_HANDLERS["tp_updateOptionPreview"]();
  }

  symbolModeRadio.addEventListener("change", updateModeDisplay);
  detailsModeRadio.addEventListener("change", updateModeDisplay);

  // Input field handlers for live preview (tolerant, don't parse/rebuild until inputs are valid enough)
  const symbolInput = document.getElementById("option-symbol-input") as HTMLInputElement;
  const detailsUnderlying = document.getElementById("option-details-underlying") as HTMLInputElement;
  const detailsRight = document.getElementById("option-details-right") as HTMLSelectElement;
  const detailsExpiration = document.getElementById("option-details-expiration") as HTMLInputElement;
  const detailsStrike = document.getElementById("option-details-strike") as HTMLInputElement;

  symbolInput.addEventListener("input", () => {
    void COMMAND_HANDLERS["tp_updateOptionPreview"]();
  });

  detailsUnderlying.addEventListener("input", () => {
    void COMMAND_HANDLERS["tp_updateOptionPreview"]();
  });
  detailsRight.addEventListener("change", () => {
    void COMMAND_HANDLERS["tp_updateOptionPreview"]();
  });
  detailsExpiration.addEventListener("input", () => {
    void COMMAND_HANDLERS["tp_updateOptionPreview"]();
  });
  detailsStrike.addEventListener("input", () => {
    void COMMAND_HANDLERS["tp_updateOptionPreview"]();
  });

  // Initial mode display
  updateModeDisplay();
}

function initializeChainBuilderUI(): void {
  // Populate fields checklist
  const checklistContainer = document.getElementById("chain-fields-checklist")!;
  checklistContainer.innerHTML = "";

  for (const fieldKey of OPTION_ENTITY_V1_FIELDS) {
    // Skip core identity fields that are already in the entity
    if (fieldKey === "underlying" || fieldKey === "expiration" || fieldKey === "strike" || fieldKey === "right" || fieldKey === "display_name" || fieldKey === "occ_symbol") {
      continue;
    }

    const propertyName = getOptionPropertyName(fieldKey);
    const isDefault = DEFAULT_CHAIN_FIELD_KEYS.includes(fieldKey);

    const label = document.createElement("label");
    label.className = "ms-font-m";
    label.style.display = "block";
    label.style.marginBottom = "4px";

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.value = fieldKey;
    checkbox.checked = isDefault;
    checkbox.id = "chain-field-" + fieldKey;

    label.appendChild(checkbox);
    label.appendChild(document.createTextNode(" " + propertyName));

    checklistContainer.appendChild(label);
  }

  // Wire up insert button
  document.getElementById("chain-insert-btn")!.onclick = () => {
    void insertChain();
  };

  // Wire up preview updates
  const strikeMinInput = document.getElementById("chain-strike-min") as HTMLInputElement;
  const strikeMaxInput = document.getElementById("chain-strike-max") as HTMLInputElement;
  const strikeStepInput = document.getElementById("chain-strike-step") as HTMLInputElement;
  const callCheckbox = document.getElementById("chain-right-call") as HTMLInputElement;
  const putCheckbox = document.getElementById("chain-right-put") as HTMLInputElement;

  strikeMinInput.addEventListener("input", () => {
    void updateChainPreview();
  });
  strikeMaxInput.addEventListener("input", () => {
    void updateChainPreview();
  });
  strikeStepInput.addEventListener("input", () => {
    void updateChainPreview();
  });
  callCheckbox.addEventListener("change", () => {
    void updateChainPreview();
  });
  putCheckbox.addEventListener("change", () => {
    void updateChainPreview();
  });

  // Initial preview
  void updateChainPreview();
}
